import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile, updatePassword, deleteAccount, exportUserData, guidesAPI } from '../services/api';
import { User, Lock, Save, AlertCircle, CheckCircle, FileText, Plus, X, ExternalLink, Phone, Trash2, Download, Image } from 'lucide-react';
import { GuideProfile } from '../types/guide';
import PasswordInput from '../components/PasswordInput';
import { validatePasswordStrength } from '../utils/passwordValidator';

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    bio: '',
    phone: '',
    displayName: '',
    iconUrl: ''
  });
  const [socialMediaLinks, setSocialMediaLinks] = useState<Array<{ platform: string; url: string }>>([]);
  
  // Guide profile state
  const [guideProfile, setGuideProfile] = useState<GuideProfile | null>(null);
  const [isGuide, setIsGuide] = useState(false);
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // UI state
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);

  // Initialize form with user data and load guide profile if applicable
  useEffect(() => {
    const loadProfile = async () => {
      if (user) {
        setProfileForm({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          bio: user.bio || '',
          phone: user.phone || '',
          displayName: user.displayName || '',
          iconUrl: user.iconUrl || ''
        });
        setSocialMediaLinks(user.socialMediaLinks || []);
        
        // Check if user is a guide and load their guide profile
        if (user.role === 'guide') {
          setIsGuide(true);
          try {
            const guide = await guidesAPI.getGuideByUserId(user.id);
            setGuideProfile(guide);
            // If user doesn't have a displayName, use guide's display_name
            if (!user.displayName) {
              setProfileForm(prev => ({
                ...prev,
                displayName: guide.display_name || ''
              }));
            }
          } catch (error: any) {
            // If 404, user doesn't have a guide profile yet (that's okay)
            if (error.response?.status !== 404) {
              console.error('Error loading guide profile:', error);
            }
          }
        }
      }
    };
    
    loadProfile();
  }, [user]);

  const handleProfileInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (profileError) setProfileError(null);
    if (profileSuccess) setProfileSuccess(null);
  };

  const addSocialMediaLink = () => {
    setSocialMediaLinks([...socialMediaLinks, { platform: '', url: '' }]);
  };

  const removeSocialMediaLink = (index: number) => {
    setSocialMediaLinks(socialMediaLinks.filter((_, i) => i !== index));
  };

  const updateSocialMediaLink = (index: number, field: 'platform' | 'url', value: string) => {
    const updated = [...socialMediaLinks];
    updated[index] = { ...updated[index], [field]: value };
    setSocialMediaLinks(updated);
  };

  const handlePasswordInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (passwordError) setPasswordError(null);
    if (passwordSuccess) setPasswordSuccess(null);
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!profileForm.firstName.trim() || !profileForm.lastName.trim()) {
      setProfileError('First name and last name are required');
      return;
    }
    
    // Validate display name if provided
    if (profileForm.displayName.trim() && profileForm.displayName.trim().length < 2) {
      setProfileError('Display name must be at least 2 characters');
      return;
    }

    setIsProfileLoading(true);
    setProfileError(null);

    try {
      // Update user profile
      await updateUserProfile({
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
        displayName: profileForm.displayName.trim() || undefined,
        bio: profileForm.bio.trim(),
        phone: profileForm.phone.trim(),
        iconUrl: profileForm.iconUrl.trim() || undefined,
        socialMediaLinks: socialMediaLinks.filter(link => link.platform && link.url)
      });

      // Update guide profile if user is a guide and displayName is provided
      if (isGuide && profileForm.displayName.trim()) {
        if (guideProfile) {
          // Update existing guide profile
          await guidesAPI.updateGuideProfile(guideProfile.id.toString(), {
            display_name: profileForm.displayName.trim()
          });
        } else {
          // Create new guide profile
          const newGuide = await guidesAPI.createGuideProfile({
            display_name: profileForm.displayName.trim()
          });
          setGuideProfile(newGuide);
        }
      }

      setProfileSuccess('Profile updated successfully!');
      
      // Re-login to refresh user data in context
      if (user?.email) {
        // Note: This is a simple approach. In a production app, you might want to
        // update the auth context more elegantly or fetch fresh user data
        window.location.reload();
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update profile';
      setProfileError(errorMessage);
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All password fields are required');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    // Validate password strength
    const passwordValidation = validatePasswordStrength(passwordForm.newPassword);
    if (!passwordValidation.valid) {
      let errorMsg = `Password does not meet strength requirements. `;
      if (passwordValidation.feedback?.warning) {
        errorMsg += passwordValidation.feedback.warning;
      }
      if (passwordValidation.feedback?.suggestions && passwordValidation.feedback.suggestions.length > 0) {
        errorMsg += ` Suggestions: ${passwordValidation.feedback.suggestions.join('. ')}`;
      }
      setPasswordError(errorMsg);
      return;
    }

    setIsPasswordLoading(true);
    setPasswordError(null);

    try {
      const response = await updatePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      // If backend returns a new token, update the stored token to keep session active
      if (response.token) {
        localStorage.setItem('token', response.token);
      }

      setPasswordSuccess('Password updated successfully!');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error: any) {
      console.error('Error updating password:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update password';
      setPasswordError(errorMessage);
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleteLoading(true);
    setDeleteError(null);

    try {
      await deleteAccount();
      
      // Logout and redirect to home page after successful deletion
      await logout();
      navigate('/creator');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete account';
      setDeleteError(errorMessage);
      setShowDeleteConfirmation(false);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleExportData = async () => {
    setIsExportLoading(true);
    setExportError(null);
    setExportSuccess(null);

    try {
      const data = await exportUserData();
      
      // Create a blob from the JSON data
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setExportSuccess('Data exported successfully!');
    } catch (error: any) {
      console.error('Error exporting data:', error);
      const errorMessage = error.response?.data?.error || 'Failed to export data';
      setExportError(errorMessage);
    } finally {
      setIsExportLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400 dark:text-gray-500">You must be logged in to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-800 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Profile Settings</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">
              Manage your account settings and preferences
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profile'
                    ? 'border-primary-500 dark:border-primary-400 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                }`}
              >
                <User className="h-4 w-4 inline mr-2" />
                Profile Information
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'password'
                    ? 'border-primary-500 dark:border-primary-400 text-primary-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                }`}
              >
                <Lock className="h-4 w-4 inline mr-2" />
                Change Password
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Personal Information</h3>
                  
                  {profileError && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                        <p className="text-sm text-red-700">{profileError}</p>
                      </div>
                    </div>
                  )}

                  {profileSuccess && (
                    <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-md p-4">
                      <div className="flex">
                        <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                        <p className="text-sm text-green-700">{profileSuccess}</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={profileForm.firstName}
                        onChange={handleProfileInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:border-primary-400"
                        placeholder="Enter your first name"
                      />
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={profileForm.lastName}
                        onChange={handleProfileInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:border-primary-400"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={profileForm.email}
                      readOnly
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      placeholder="Email address"
                    />
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
                      Email address cannot be changed. Contact support if you need to update your email.
                    </p>
                  </div>

                  {/* Display Name field for all users */}
                  <div>
                    <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Display Name
                    </label>
                    <input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={profileForm.displayName}
                      onChange={handleProfileInputChange}
                      className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:border-primary-400"
                      placeholder="Enter your public display name"
                      maxLength={100}
                    />
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
                      This is the name that will be displayed publicly when you create content. {profileForm.displayName.length}/100 characters
                    </p>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Phone Number
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={profileForm.phone}
                        onChange={handleProfileInputChange}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:border-primary-400"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Bio/Description
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute top-3 left-0 pl-3 pointer-events-none">
                        <FileText className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                      <textarea
                        id="bio"
                        name="bio"
                        rows={4}
                        value={profileForm.bio}
                        onChange={handleProfileInputChange}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:border-primary-400 resize-none"
                        placeholder="Tell us about yourself..."
                      />
                    </div>
                  </div>

                  {/* Icon URL field */}
                  <div>
                    <label htmlFor="iconUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Icon URL
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Image className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                      <input
                        type="url"
                        id="iconUrl"
                        name="iconUrl"
                        value={profileForm.iconUrl}
                        onChange={handleProfileInputChange}
                        className="block w-full pl-10 pr-20 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:border-primary-400"
                        placeholder="https://example.com/icon.png"
                      />
                      {profileForm.iconUrl && (
                        <button
                          type="button"
                          onClick={() => setProfileForm(prev => ({ ...prev, iconUrl: '' }))}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-300"
                          title="Clear icon URL"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">
                      Enter a URL to an icon/avatar image. Leave empty to use default.
                    </p>
                  </div>

                  {/* Social Media Links Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Social Media Links
                      </label>
                      <button
                        type="button"
                        onClick={addSocialMediaLink}
                        className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md text-primary-600 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Link
                      </button>
                    </div>
                    {socialMediaLinks.map((link, index) => (
                      <div key={index} className="flex space-x-2 mb-3">
                        <div className="relative flex-1">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <ExternalLink className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          </div>
                          <input
                            type="text"
                            placeholder="Platform (e.g., Twitter, LinkedIn)"
                            value={link.platform}
                            onChange={(e) => updateSocialMediaLink(index, 'platform', e.target.value)}
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:border-primary-400 sm:text-sm"
                          />
                        </div>
                        <div className="flex-2">
                          <input
                            type="url"
                            placeholder="URL (https://...)"
                            value={link.url}
                            onChange={(e) => updateSocialMediaLink(index, 'url', e.target.value)}
                            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:border-primary-400 sm:text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSocialMediaLink(index)}
                          className="inline-flex items-center px-2 py-2 border border-red-200 rounded-md text-red-600 bg-red-50 hover:bg-red-100"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    {socialMediaLinks.length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 italic">No social media links added yet.</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isProfileLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProfileLoading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    <Save className="h-4 w-4 mr-2" />
                    {isProfileLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>

                {/* Export Data Section */}
                <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Export Your Data</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 mb-4">
                    Download all your profile information and content you've created as a JSON file.
                  </p>

                  {exportSuccess && (
                    <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-md p-4">
                      <div className="flex">
                        <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                        <p className="text-sm text-green-700">{exportSuccess}</p>
                      </div>
                    </div>
                  )}

                  {exportError && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                        <p className="text-sm text-red-700">{exportError}</p>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleExportData}
                    disabled={isExportLoading}
                    className="inline-flex items-center px-4 py-2 border border-primary-300 text-sm font-medium rounded-md text-primary-700 bg-white dark:bg-gray-800 hover:bg-primary-50 dark:bg-primary-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isExportLoading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-700 mr-2"></div>
                    )}
                    <Download className="h-4 w-4 mr-2" />
                    {isExportLoading ? 'Exporting...' : 'Export My Data'}
                  </button>
                </div>

                {/* Delete Account Section */}
                <div className="pt-6 mt-6 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Danger Zone</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500 mb-4">
                    Once you delete your account, there is no going back. This action is permanent and will delete all your data.
                  </p>

                  {deleteError && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                        <p className="text-sm text-red-700">{deleteError}</p>
                      </div>
                    </div>
                  )}

                  {!showDeleteConfirmation ? (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirmation(true)}
                      className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white dark:bg-gray-800 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Account
                    </button>
                  ) : (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-red-800 mb-2">
                            Are you absolutely sure?
                          </h4>
                          <p className="text-sm text-red-700 mb-4">
                            This will permanently delete your account and all associated data including bookings, reviews, and cards. This action cannot be undone.
                          </p>
                          <div className="flex space-x-3">
                            <button
                              type="button"
                              onClick={handleDeleteAccount}
                              disabled={isDeleteLoading}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isDeleteLoading && (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              )}
                              {isDeleteLoading ? 'Deleting...' : 'Yes, Delete My Account'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowDeleteConfirmation(false);
                                setDeleteError(null);
                              }}
                              disabled={isDeleteLoading}
                              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            )}

            {activeTab === 'password' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Change Password</h3>
                  
                  {passwordError && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                        <p className="text-sm text-red-700">{passwordError}</p>
                      </div>
                    </div>
                  )}

                  {passwordSuccess && (
                    <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-md p-4">
                      <div className="flex">
                        <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                        <p className="text-sm text-green-700">{passwordSuccess}</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Current Password *
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:border-primary-400"
                        placeholder="Enter your current password"
                      />
                    </div>

                    <div>
                      <PasswordInput
                        id="newPassword"
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordInputChange}
                        placeholder="Enter your new password"
                        label="New Password"
                        showLabel={true}
                        autoComplete="new-password"
                        required
                        showStrengthIndicator={true}
                      />
                    </div>

                    <div>
                      <PasswordInput
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordInputChange}
                        placeholder="Confirm your new password"
                        label="Confirm New Password"
                        showLabel={true}
                        autoComplete="new-password"
                        required
                        showStrengthIndicator={false}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isPasswordLoading}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPasswordLoading && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    <Lock className="h-4 w-4 mr-2" />
                    {isPasswordLoading ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;